'use client';
import { Grid } from '@mui/material';
import { trackEvent, type sendEventProps } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { startTransition, useEffect } from 'react';
import { ErrorPage } from '../error';

type RouteErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
  app: sendEventProps['app'];
};

/**
 * Body for an app's `error.tsx` boundary: reports the error to New Relic and renders ErrorPage
 * with a retry that refetches the route and resets the boundary.
 *
 * @param {Error} error - The error caught by the boundary.
 * @param {Function} reset - Next.js boundary reset.
 * @param {string} app - The app reporting the error.
 * @returns {React.JSX.Element} The rendered error boundary body.
 */
export const RouteError = ({ error, reset, app }: RouteErrorProps) => {
  const router = useRouter();

  useEffect(() => {
    trackEvent({
      app,
      type: 'error',
      subtype: 'routeError',
      severity: 'high',
      data: { message: error.message, digest: error.digest, path: window.location.pathname },
    });
  }, [app, error]);

  return (
    <Grid container sx={{ flexDirection: 'column', justifyContent: 'center', height: '100vh' }}>
      <ErrorPage
        onRetry={() =>
          startTransition(() => {
            router.refresh();
            reset();
          })
        }
      />
    </Grid>
  );
};

/**
 * Body for an app's `global-error.tsx`. It replaces the root layout, so it renders its own
 * html and body and avoids theme-dependent components.
 *
 * @param {Error} error - The error caught by the boundary.
 * @param {string} app - The app reporting the error.
 * @returns {React.JSX.Element} The rendered global error document.
 */
export const GlobalError = ({ error, app }: Omit<RouteErrorProps, 'reset'>) => {
  useEffect(() => {
    trackEvent({
      app,
      type: 'error',
      subtype: 'globalError',
      severity: 'high',
      data: { message: error.message, digest: error.digest, path: window.location.pathname },
    });
  }, [app, error]);

  return (
    <html lang="es">
      <body style={{ fontFamily: 'sans-serif', display: 'grid', placeItems: 'center', minHeight: '100vh', margin: 0 }}>
        <div style={{ textAlign: 'center' }}>
          <h1>¡Ups! Algo salió mal</h1>
          <button type="button" onClick={() => window.location.reload()}>
            Reintentar
          </button>
        </div>
      </body>
    </html>
  );
};
